/**
 * Event coalescer — batches identical events within a sliding window,
 * emits one combined event with a count when the window closes.
 *
 * Use case: high-frequency event streams where downstream consumers
 * (chat, UI, log files) need de-spammed output. Combat-log auto-attack
 * spam is the canonical user — 4 bots brawling generate ~80 damage
 * broadcasts/30s; without coalescing the chat panel scrolls past
 * faster than humans can read.
 *
 * Lifted from `RateLimitedLogger` (src/utils/logger/SpecializedLoggers.ts:70)
 * but with a different semantic: that class DROPS events past cooldown,
 * this one ACCUMULATES identical events into a single emit. Both are
 * keyed by a user-supplied key, both use a per-key timer/cooldown map.
 *
 * Pure module — no DOM / no React / no game-side deps. Generic over
 * the event payload `T` so future consumers (e.g. floating-text emitter
 * under heavy AOE; future debuff-stack notifications) reuse without
 * reinvention.
 *
 * Semantics:
 *   - First push for a key: schedules a flush at ts + windowMs, count=1
 *   - Subsequent pushes within the window for the same key: increment
 *     count, replace sample with the latest event payload (so the
 *     formatter renders the latest amount/ability)
 *   - Flush fires emit(sample, count); slot resets
 *   - flush() forces an immediate emit of all pending slots (used on
 *     dispose / room-leave so messages aren't lost)
 *
 * NOT thread-safe — designed for single-threaded JS event loops.
 */

export interface Coalescer<T> {
  /**
   * Add an event. If a slot for `keyOf(event)` already has pending
   * events within the window, accumulate; otherwise start a new slot.
   */
  push(event: T): void;
  /** Force-emit all pending slots immediately. */
  flush(): void;
  /**
   * Cancel all pending timers WITHOUT emitting. For test cleanup +
   * room-disconnect paths where late events are noise.
   */
  dispose(): void;
}

export interface CoalescerOptions<T> {
  /** Key derivation — events with the same key collapse together. */
  keyOf: (event: T) => string;
  /** Coalesce window in ms. Identical events arriving within this
   *  window of the slot's first event accumulate. */
  windowMs: number;
  /** Sink — called once per slot when the window closes. */
  emit: (event: T, count: number) => void;
  /**
   * Optional clock injection for tests. Defaults to `Date.now`.
   * NOTE: tests that drive the timer should also pass a custom
   * setTimeout-like via `schedule` — see test fixtures.
   */
  now?: () => number;
  /**
   * Optional scheduler injection. Defaults to global `setTimeout`.
   * Returns a cancel function. Tests pass a fake-timer-aware version.
   */
  schedule?: (cb: () => void, ms: number) => () => void;
}

interface Slot<T> {
  sample: T;
  count: number;
  /** Returned by the schedule()'d call so we can cancel on dispose. */
  cancel: () => void;
}

export function createCoalescer<T>(opts: CoalescerOptions<T>): Coalescer<T> {
  const slots = new Map<string, Slot<T>>();
  const schedule =
    opts.schedule ??
    ((cb: () => void, ms: number) => {
      const handle = setTimeout(cb, ms);
      return () => clearTimeout(handle);
    });

  const flushKey = (key: string) => {
    const slot = slots.get(key);
    if (!slot) return;
    slots.delete(key);
    opts.emit(slot.sample, slot.count);
  };

  return {
    push(event: T): void {
      const key = opts.keyOf(event);
      const existing = slots.get(key);
      if (existing) {
        existing.count += 1;
        existing.sample = event;
        return;
      }
      const cancel = schedule(() => flushKey(key), opts.windowMs);
      slots.set(key, { sample: event, count: 1, cancel });
    },

    flush(): void {
      // Snapshot keys because flushKey mutates the map.
      const keys = [...slots.keys()];
      for (const key of keys) {
        const slot = slots.get(key);
        if (slot) slot.cancel();
        flushKey(key);
      }
    },

    dispose(): void {
      for (const slot of slots.values()) slot.cancel();
      slots.clear();
    },
  };
}
